/**
 * Animation controller for wave movement and tournament cycles
 */

import { WAVE_SPEED_VH, WAVE_FALLOFF_VH } from "./constants";

export class AnimationController {
  private waveY: number;
  private lastTimestamp: number;
  private canvasHeight: number;
  private onNewTournament: () => void;
  private waveSpeed: number;
  private waveFalloff: number;

  constructor(canvasHeight: number, onNewTournament: () => void) {
    this.canvasHeight = canvasHeight;
    // Convert vh to pixels based on canvas height
    this.waveFalloff = (WAVE_FALLOFF_VH / 100) * canvasHeight;
    this.waveSpeed = (WAVE_SPEED_VH / 100) * canvasHeight;
    this.waveY = canvasHeight + this.waveFalloff;
    this.lastTimestamp = 0;
    this.onNewTournament = onNewTournament;
  }

  /**
   * Updates animation state based on elapsed time
   */
  update(timestamp: number): number {
    if (this.lastTimestamp === 0) {
      this.lastTimestamp = timestamp;
      return this.waveY;
    }

    const deltaTime = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    // Update wave position
    this.waveY -= deltaTime * this.waveSpeed;

    // Check if wave has completed cycle
    if (this.waveY < -this.waveFalloff * 2) {
      this.reset();
      this.onNewTournament();
    }

    return this.waveY;
  }

  /**
   * Resets wave to starting position
   */
  reset(): void {
    this.waveY = this.canvasHeight + this.waveFalloff;
  }

  /**
   * Updates canvas height (for resize)
   */
  setCanvasHeight(height: number): void {
    this.canvasHeight = height;
    // Recalculate pixel values from vh
    this.waveFalloff = (WAVE_FALLOFF_VH / 100) * height;
    this.waveSpeed = (WAVE_SPEED_VH / 100) * height;
    // Adjust wave position proportionally
    const progress = this.getProgress();
    this.waveY = height + this.waveFalloff - progress * (height + this.waveFalloff * 3);
  }

  /**
   * Gets current progress through animation cycle (0-1)
   */
  getProgress(): number {
    const totalDistance = this.canvasHeight + this.waveFalloff * 3;
    const traveled = this.canvasHeight + this.waveFalloff - this.waveY;
    return Math.max(0, Math.min(1, traveled / totalDistance));
  }

  /**
   * Gets current wave Y position
   */
  getWaveY(): number {
    return this.waveY;
  }
}

/**
 * Creates and manages animation loop
 */
export class AnimationLoop {
  private animationId: number | null = null;
  private controller: AnimationController;
  private onFrame: (waveY: number, timestamp: number) => void;

  constructor(
    controller: AnimationController,
    onFrame: (waveY: number, timestamp: number) => void,
  ) {
    this.controller = controller;
    this.onFrame = onFrame;
  }

  /**
   * Starts the animation loop
   */
  start(): void {
    if (this.animationId !== null) return;

    const animate = (timestamp: number) => {
      const waveY = this.controller.update(timestamp);
      this.onFrame(waveY, timestamp);
      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  /**
   * Stops the animation loop
   */
  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Checks if animation is running
   */
  isRunning(): boolean {
    return this.animationId !== null;
  }
}

/**
 * Performance monitoring utilities
 */
export class PerformanceMonitor {
  private frameTimes: number[] = [];
  private maxSamples: number = 60;

  /**
   * Records frame time
   */
  recordFrame(deltaTime: number): void {
    this.frameTimes.push(deltaTime);
    if (this.frameTimes.length > this.maxSamples) {
      this.frameTimes.shift();
    }
  }

  /**
   * Gets average FPS
   */
  getFPS(): number {
    if (this.frameTimes.length === 0) return 0;
    const avgFrameTime =
      this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    return avgFrameTime > 0 ? 1000 / avgFrameTime : 0;
  }

  /**
   * Checks if performance is acceptable
   */
  isPerformanceGood(): boolean {
    return this.getFPS() >= 30;
  }
}
